import { z } from 'zod';

/** Spec 2026-10-15-ugyfel-kep-csatolmany: one source for every limit. */
export const CLIENT_ATTACHMENT_LIMITS = {
  perMessage: 5,
  maxBytes: 10 * 1024 * 1024,
  maxPixels: 50_000_000,
  perBatch: 50,
  cleanEdge: 2560,
} as const;

export const ClientAttachmentStatusSchema = z.enum(['pending', 'ready', 'rejected']);
export type ClientAttachmentStatus = z.infer<typeof ClientAttachmentStatusSchema>;

/** What the client's own page may know about an attachment. */
export const PublicClientAttachmentSchema = z.object({
  id: z.string().guid(),
  status: ClientAttachmentStatusSchema,
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
});
export type PublicClientAttachment = z.infer<typeof PublicClientAttachmentSchema>;

/** Staff view adds whether it was taken into the media library. */
export const ClientAttachmentSchema = PublicClientAttachmentSchema.extend({
  promotedMediaId: z.string().guid().nullable(),
});
export type ClientAttachment = z.infer<typeof ClientAttachmentSchema>;

export const UploadedClientAttachmentSchema = z.object({
  id: z.string().guid(),
  status: ClientAttachmentStatusSchema,
});
export type UploadedClientAttachment = z.infer<typeof UploadedClientAttachmentSchema>;

export const AttachmentIdsSchema = z
  .array(z.string().guid())
  .max(CLIENT_ATTACHMENT_LIMITS.perMessage)
  .optional();

const PREFIX = 'client-attachments/';

/** Private-bucket key layout, shared by the API (upload) and the worker. */
export const clientAttachmentKeys = {
  prefix: PREFIX,
  original: (batchId: string, id: string, ext: string): string =>
    `${PREFIX}${batchId}/${id}/original.${ext}`,
  clean: (batchId: string, id: string): string => `${PREFIX}${batchId}/${id}/clean.webp`,
  /** `client-attachments/<batch>/<id>/<file>` → `<id>`; anything else → null. */
  idOf: (key: string): string | null => {
    if (!key.startsWith(PREFIX)) return null;
    const parts = key.slice(PREFIX.length).split('/');
    return parts.length === 3 && parts[1] ? parts[1] : null;
  },
};
